/* import __COLOCATED_TEMPLATE__ from './localized-image.hbs.js'; */
var _dec, _dec2, _class, _class2, _descriptor, _descriptor2, _descriptor3, _class3;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import Component from '@ember/component';
import { inject as service } from '@ember/service';
import { computed } from '@ember/object';
import { tagName } from '@ember-decorators/component';
import Logger from '@square/dashboard-utilities/utils/logger';
import Enum from '@square/dashboard-utilities/utils/enum';
import environment from '@square/environment-utils/environment';
import * as Sentry from '@sentry/ember';

/**
 * A hash of all the default locales, one per country. Please keep this alphabetized by key!
 * @type {Object}
 */
const DefaultLocale = {
  AU: 'en-AU',
  CA: 'en-CA',
  ES: 'es-ES',
  FR: 'fr-FR',
  GB: 'en-GB',
  IE: 'en-IE',
  JP: 'ja-JP',
  US: 'en-US'
};
const MissingAssetPlaceholder = {
  '1x': '/@dashboard/shared-ui/images/missing-image@1x.png',
  '2x': '/@dashboard/shared-ui/images/missing-image@2x.png'
};

/**
 * `LocalizedImage` is designed to be used as drop-in replacement for `ResponsiveImage` to add support for image
 * localization.
 *
 * For an in-depth overview of this localization system (originally pitched as a helper), check out the original proposal:
 * https://docs.google.com/document/d/1hvOyNsDDxZKpZn8QFYxe3hDKST5ga0oIczs8NV_GA3c/edit#
 *
 * @example
 * <LocalizedImage @directory="/01-dashboard-shared/images/retail"
 *                 @imageName="retail-welcome-modal-register"
 *                 @description="The Retail welcome modal register"
 *                 @width={{400}}
 *                 @height={{300}} />
 *
 * @param {string} directory - The directory the image is located in.
 * @param {string} imageName - The name of the image.
 * @param {Integer} width - Width for the image.
 * @param {Integer} height - Height for the image.
 * @param {string} description - Descriptive text for the image (`alt` text).
 * @param {string} [extension='png'] - The extension to be used when generating the image path.
 * @param {boolean} [supportsMultipleSizes=true] - If `false`, `LocalizedImage` won't generate a 1x/2x source pair, and
 *   won't append size parts (i.e., '@2x') to the image path. This provides support for images that don't have multiple
 *   sizes to choose from.
 * @param {boolean} [allowFallbackToUs=true] - If `true`, `LocalizedImage` will choose the `en-US` locale as a final
 *   fallback to use if all else fails. Disable this is situations where showing `en-US` content might not be desirable.
 * @param {boolean} [isExternal=false] - Set to `true` when specifying a URL for images directly uploaded to third party
 *   storage solutions, such as S3. For further context, search Dashboard for usages.
 */
let LocalizedImage = (_dec = tagName(''), _dec2 = computed('bestFitLocale', 'shouldShowMissingAssetPlaceholder'), _dec(_class = (_class2 = (_class3 = class LocalizedImage extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "assetMap", _descriptor, this);
    _initializerDefineProperty(this, "userInfo", _descriptor2, this);
    _initializerDefineProperty(this, "i18n", _descriptor3, this);
    _defineProperty(this, "isLoading", true);
    _defineProperty(this, "bestFitLocale", null);
    _defineProperty(this, "directory", null);
    _defineProperty(this, "imageName", null);
    _defineProperty(this, "extension", 'png');
    _defineProperty(this, "supportsMultipleSizes", true);
    _defineProperty(this, "allowFallbackToUS", true);
    _defineProperty(this, "isExternal", false);
    _defineProperty(this, "queryCache", {});
  }
  get isLoggingEnabled() {
    const queryParam = new URLSearchParams(window.location.search).get('logLocalizedImage');
    return queryParam === 'true';
  }
  get shouldShowMissingAssetPlaceholder() {
    return !this.isLoading && !this.bestFitLocale;
  }
  get src() {
    return this.shouldShowMissingAssetPlaceholder ? MissingAssetPlaceholder : this.createSrcMapWithLocale(this.bestFitLocale);
  }

  /**
   * Determines the "best fit" locale according to the standardized localization algorithm.
   *
   * The localization algorithm:
   *   1. `LocalizedImage` will try the locale created by joining the user’s preferred language code and the user’s country code
   *      (this is the country that is associated with their account): <preferred language code>-<current country code>.
   *      The language code will be pulled from the user’s browser, and the country code will be pulled from the user’s
   *      account. If an image exists for this, serve that.
   *   2. If #1 fails, `LocalizedImage` will check if a default image variant exists for the user’s current country, which is
   *      determined by checking the default locale list (hash of all default locales, one per country). If an image exists
   *      for this, serve that.
   *   3. If the checks above fail, then `LocalizedImage` will attempt to serve an image with the fallback locale of en-US
   *      if allowed.
   *
   * If a best-fit locale could not be determined, `bestFitLocale` will remain `null`, and `LocalizedImage` will render a
   * missing image placeholder.
   */
  async findBestFitLocale() {
    const browserLocaleParts = this.userInfo.browserLanguage.split('-');

    // 1) User's preferred language code + user's current country code.
    const preferredLocale = `${browserLocaleParts[0]}-${this.userInfo.countryCode}`;
    if (await this.doesAssetExistWithLocale(preferredLocale)) {
      this.logSelectionOfLocale(LocalizedImage.LocaleType.preferred, preferredLocale);
      return preferredLocale;
    }

    // 2) Default locale given current users country code.
    const defaultLocale = DefaultLocale[this.userInfo.countryCode];
    if (await this.doesAssetExistWithLocale(defaultLocale)) {
      this.logSelectionOfLocale(LocalizedImage.LocaleType.default, defaultLocale);
      return defaultLocale;
    }

    // 3) Fallback locale if allowed.
    if (this.allowFallbackToUS) {
      this.logSelectionOfLocale(LocalizedImage.LocaleType.fallback, DefaultLocale.US);
      return DefaultLocale.US;
    }
    return null;
  }
  didReceiveAttrs() {
    super.didReceiveAttrs();
    if (this.assetMap.prepend !== '' && this.directory[0] === '/') {
      this.directory = this.directory.slice(1);
    }

    // Initiate async loading of the locale. In production this would all be instantaneous because no fetch requests
    // would be made to the local server, and all methods would return already-resolved promises.
    this.findBestFitLocale().then(locale => {
      if (this.isDestroyed || this.isDestroying) {
        return;
      }
      this.set('isLoading', false);
      if (locale) {
        this.set('bestFitLocale', locale);
      } else {
        const message = `A best-fit locale could not be found for { countryCode: ${this.userInfo.countryCode}, browserLanguage: ${this.userInfo.browserLanguage} }`;
        Sentry.captureMessage('A best-fit locale could not be found');
        Logger.warn(message);
      }
    });
  }
  createSrcMapWithLocale(locale) {
    const shouldIncludeFingerprint = !this.isExternal;
    if (!this.supportsMultipleSizes) {
      return {
        '1x': this.createLocalizedImagePath(locale, null, shouldIncludeFingerprint)
      };
    }
    return {
      '1x': this.createLocalizedImagePath(locale, '1x', shouldIncludeFingerprint),
      '2x': this.createLocalizedImagePath(locale, '2x', shouldIncludeFingerprint)
    };
  }
  createLocalizedImagePath(locale, scale, shouldIncludeFingerprint = true) {
    const localizedImagePath = `${this.directory}/${this.createImageNameWithLocale(locale, scale)}`;
    return shouldIncludeFingerprint ? this.assetMap.resolve(localizedImagePath) : localizedImagePath;
  }
  createImageNameWithLocale(locale, scale = null) {
    const scalePart = scale !== null ? `@${scale}` : '';
    return `${this.imageName}--${locale}${scalePart}.${this.extension}`;
  }

  /**
   * Checks the `assetMap` service to see if an asset with the specified locale exists.
   * @param {?string} locale - A locale used to check for the existence of an asset.
   * @returns {Promise} A `Promise` whose value will indicate whether or not the asset exists in the asset map.
   */
  doesAssetExistWithLocale(locale) {
    if (!locale) {
      return Promise.resolve(false);
    }
    const scale = this.supportsMultipleSizes ? '1x' : null;
    const fullImagePathWithoutFingerprint = this.createLocalizedImagePath(locale, scale, false);

    // NB(tbiesecker): Due to the fact that the `assetMap` service is disabled during development, a workaround is
    // needed to simulate the functionality that would otherwise have been provided by the `assetMap` service. In
    // development environments, we simply query the local server for the asset to determine if it exists or not. This
    // should add almost no overhead and will allow developers to see how the `LocalizedImage` component would behave in
    // production. Note, however, that synchronous network calls are deprecated, and a better solution should be found.
    // Ideally, we would figure out how to enable the `assetMap` service in local development and therefore remove the
    // need for any workaround.
    // TODO(RETAIL-11394): Enable the assetMap service in local development and remove this workaround.
    if (this.isExternal || !environment().isTesting && environment().isDevelopment) {
      const path = this.isExternal ? fullImagePathWithoutFingerprint : `/${fullImagePathWithoutFingerprint}`;
      return this.queryServerForExistenceOfAssetWithPath(path);
    }
    return Promise.resolve(this.assetMap.fullMap?.[fullImagePathWithoutFingerprint] !== undefined);
  }

  /**
   * @param {LocalizedImage.LocaleType} localeType - The type of locale.
   * @param {string} locale - The locale that was selected.
   */
  logSelectionOfLocale(localeType, locale) {
    if (this.isLoggingEnabled) {
      Logger.info(`LocalizedImage selected: { localeType: ${localeType}, locale: ${locale}, image: ${this.imageName} }`);
    }
  }
  async queryServerForExistenceOfAssetWithPath(path) {
    if (this.queryCache[path] !== undefined) {
      return this.queryCache[path];
    }
    const response = await fetch(path);
    this.queryCache[path] = response.status === 200;
    return this.queryCache[path];
  }
}, _defineProperty(_class3, "LocaleType", new Enum(['default', 'preferred', 'fallback'])), _class3), _descriptor = _applyDecoratedDescriptor(_class2.prototype, "assetMap", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class2.prototype, "userInfo", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class2.prototype, "i18n", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class2.prototype, "src", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "src"), _class2.prototype), _class2)) || _class);
export { LocalizedImage as default };